import { useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  BellIcon,
  ClapperboardIcon,
  CreditCardIcon,
  ExternalLinkIcon,
  LogOutIcon,
  MoreVerticalIcon,
  PlayIcon,
  UserCircleIcon,
} from "lucide-react"

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar"
import { supabase } from "@/lib/supabase"
import { useAuth } from "@/contexts/AuthContext"
import { useMyApps } from "@/lib/myApps"
import { GUILD_URL, VIEWER_URL } from "@/lib/appUrls"

export function NavUserSimple({
  user,
}: {
  user: {
    name: string
    email: string
    avatar: string
  }
}) {
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const { user: authUser } = useAuth()
  const apps = useMyApps(authUser?.id)

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut()
      navigate('/auth')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div className="relative">
      <button
        className="flex h-12 w-full items-center gap-2 rounded-md px-2 text-sm font-medium transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
        data-state={isOpen ? "open" : "closed"}
        onClick={() => setIsOpen(!isOpen)}
      >
        <Avatar className="h-8 w-8 rounded-lg">
          <AvatarImage src={user.avatar} alt={user.name} />
          <AvatarFallback className="rounded-lg">{getInitials(user.name)}</AvatarFallback>
        </Avatar>
        <div className="grid flex-1 text-left text-sm leading-tight">
          <span className="truncate font-semibold">{user.name}</span>
          <span className="truncate text-xs">{user.email}</span>
        </div>
        <MoreVerticalIcon className="ml-auto size-4" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute left-full bottom-0 ml-2 w-56 rounded-lg border bg-popover p-1 text-popover-foreground shadow-md z-50">
            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback className="rounded-lg">{getInitials(user.name)}</AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">{user.name}</span>
                <span className="truncate text-xs text-muted-foreground">
                  {user.email}
                </span>
              </div>
            </div>
            <div className="h-px bg-border my-1" />

            <button
              className="relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
              onClick={() => {
                setIsOpen(false)
                navigate('/profile')
              }}
            >
              <UserCircleIcon className="h-4 w-4" />
              <span>Account</span>
            </button>

            <button
              className="relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
              onClick={() => {
                setIsOpen(false)
                navigate('/billing')
              }}
            >
              <CreditCardIcon className="h-4 w-4" />
              <span>Billing</span>
            </button>

            <button
              className="relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
              onClick={() => {
                setIsOpen(false)
                navigate('/settings')
              }}
            >
              <BellIcon className="h-4 w-4" />
              <span>Notifications</span>
            </button>

            {apps && (apps.viewer || apps.guild) && (
              <>
                <div className="h-px bg-border my-1" />
                <p className="px-2 py-1 text-xs font-medium text-muted-foreground">
                  Other Kinora apps
                </p>
                {apps.viewer && (
                  <a
                    href={VIEWER_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                    onClick={() => setIsOpen(false)}
                  >
                    <PlayIcon className="h-4 w-4" />
                    <span>Open Kinora Viewer</span>
                    <ExternalLinkIcon className="ml-auto h-3.5 w-3.5 opacity-60" />
                  </a>
                )}
                {apps.guild && (
                  <a
                    href={GUILD_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground"
                    onClick={() => setIsOpen(false)}
                  >
                    <ClapperboardIcon className="h-4 w-4" />
                    <span>Open Kinora Guild</span>
                    <ExternalLinkIcon className="ml-auto h-3.5 w-3.5 opacity-60" />
                  </a>
                )}
              </>
            )}

            <div className="h-px bg-border my-1" />

            <button
              className="relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
              onClick={() => {
                setIsOpen(false)
                handleLogout()
              }}
            >
              <LogOutIcon className="h-4 w-4" />
              <span>Log out</span>
            </button>
          </div>
        </>
      )}
    </div>
  )
}
